/**
 * La trésorerie telle que l'écran la lit : la synthèse du tableau de bord et
 * la file du compte d'attente (499000).
 *
 * Tout vient des vues du journal de trésorerie (migration 20260929072901) :
 * `compta.v_tresorerie_synthese` et `compta.v_file_d_attente_499000`. Rien
 * n'est recalculé ici ; les fonctions de ce fichier ne font que ranger ce que
 * la base a compté, pour qu'un test le vérifie sans réseau.
 *
 * Le 499000 est une **file de problèmes à résoudre**, pas un cimetière
 * (orientation d'Éclosion du 28/09/2026) : chaque ligne dit pourquoi le
 * mouvement attend, depuis quand, et si un humain l'y a mis.
 */

export type Sens = 'entree' | 'sortie';

export const SENS: Record<Sens, string> = {
  entree: 'Entrées',
  sortie: 'Sorties',
};

/** Pourquoi une part attend en 499000 (colonne `raison` de l'attendu). */
export const RAISONS_D_ATTENTE: Record<string, string> = {
  hors_perimetre: 'hors périmètre',
  non_affecte: 'non affecté',
  facture_de_test: 'facture de test',
  tiers_ambigu: 'compte client à deux tiers',
  sans_client: 'sans client',
  note_inconnue: 'note de crédit introuvable',
  note_de_test: 'note sur une facture de test',
  note_de_transition_a_trancher: 'note de transition à trancher',
  note_de_transition_mixte: 'note de transition partagée (ajustement et dette)',
  sortie_sur_facture_anterieure: 'sortie sur une facture d’avant 2026',
};

/** Ce que le journal de trésorerie fait d'un mouvement (`v_tresorerie_perimetre.statut`). */
export const STATUTS_DE_MOUVEMENT: Record<string, string> = {
  comptabilise: 'comptabilisés',
  a_comptabiliser: 'à comptabiliser',
  extourne_hors_geste: 'extournés à la main : à trancher',
  retenu_doublon: 'retenus : doublon à trancher',
  retenu_recent: 'retenus : importés il y a moins de 30 minutes',
  hors_exercice: 'après le dernier exercice ouvert',
  ecarte: 'écartés du grand livre',
  source_refusee: 'sans journal (espèces, sortie Stripe)',
};

export const raisonAffichee = (r: string): string => RAISONS_D_ATTENTE[r] ?? r;

// ---------------------------------------------------------------------------
//  La synthèse
// ---------------------------------------------------------------------------

/** Une ligne de `compta.v_tresorerie_synthese`. */
export interface LigneDeSynthese {
  indicateur: string;
  sens: Sens | null;
  raison: string | null;
  nombre: number;
  montantCents: number | null;
  jusquAu: string | null;
}

export interface Compte {
  nombre: number;
  montantCents: number;
}

export interface EtatDeLaTresorerie {
  /** Le compte d'attente comptabilisé, par sens : nombre de mouvements et montant. */
  attente: Record<Sens, Compte>;
  /** Le même, par raison (de l'attendu des mouvements encodés), du plus lourd au plus léger. */
  parRaison: { sens: Sens; raison: string; nombre: number; montantCents: number }[];
  /** Les mouvements des exercices tenus, par statut. */
  mouvements: { sens: Sens; statut: string; nombre: number; montantCents: number }[];
  /** C1 : anomalies (doivent être à zéro après un passage) et signaux (à trancher). */
  anomalies: number;
  signaux: number;
  /** La dernière date de valeur encodée, par journal (ING, STR). */
  encodeJusquAu: Record<string, string | null>;
  /** Les soldes comptabilisés, en D − C, par compte (550001, 580100, 499000). */
  soldes: Record<string, number>;
}

const vide = (): Compte => ({ nombre: 0, montantCents: 0 });

export function lireLaSynthese(lignes: readonly LigneDeSynthese[]): EtatDeLaTresorerie {
  const etat: EtatDeLaTresorerie = {
    attente: { entree: vide(), sortie: vide() },
    parRaison: [],
    mouvements: [],
    anomalies: 0,
    signaux: 0,
    encodeJusquAu: {},
    soldes: {},
  };
  for (const l of lignes) {
    const montant = l.montantCents ?? 0;
    switch (l.indicateur) {
      case 'attente':
        if (l.sens) etat.attente[l.sens] = { nombre: l.nombre, montantCents: montant };
        break;
      case 'attente_par_raison':
        if (l.sens && l.raison) etat.parRaison.push({ sens: l.sens, raison: l.raison, nombre: l.nombre, montantCents: montant });
        break;
      case 'mouvements':
        if (l.sens && l.raison) etat.mouvements.push({ sens: l.sens, statut: l.raison, nombre: l.nombre, montantCents: montant });
        break;
      case 'controle':
        if (l.raison === 'anomalie') etat.anomalies = l.nombre;
        if (l.raison === 'signal') etat.signaux = l.nombre;
        break;
      case 'encode_jusqu_au':
        if (l.raison) etat.encodeJusquAu[l.raison] = l.jusquAu;
        break;
      case 'solde':
        if (l.raison) etat.soldes[l.raison] = montant;
        break;
      default:
        break;
    }
  }
  etat.parRaison.sort((a, b) => b.montantCents - a.montantCents || a.raison.localeCompare(b.raison));
  const ordre = Object.keys(STATUTS_DE_MOUVEMENT);
  etat.mouvements.sort((a, b) =>
    a.sens.localeCompare(b.sens) || ordre.indexOf(a.statut) - ordre.indexOf(b.statut));
  return etat;
}

/** Les mouvements d'un statut, entrées et sorties réunies. */
export function mouvementsDuStatut(etat: EtatDeLaTresorerie, statut: string): Compte {
  return etat.mouvements
    .filter((m) => m.statut === statut)
    .reduce((t, m) => ({ nombre: t.nombre + m.nombre, montantCents: t.montantCents + m.montantCents }), vide());
}

// ---------------------------------------------------------------------------
//  La file du compte d'attente
// ---------------------------------------------------------------------------

/** Une ligne de `compta.v_file_d_attente_499000`. */
export interface MouvementEnAttente {
  sens: Sens;
  mouvementId: string;
  ecritureId: string | null;
  journal: string | null;
  reference: string | null;
  dateValeur: string | null;
  piece: string | null;
  contrepartie: string | null;
  communication: string | null;
  montantMouvementCents: number;
  attenteCents: number;
  raisons: string[];
  categorie: string | null;
  decideParUnHumain: boolean;
  ageJours: number | null;
  aReclasser: boolean;
}

export interface FiltresAttente {
  sens: Sens | null;
  raison: string | null;
  /** Seulement ce qu'aucun humain n'a tranché : tout sauf un « hors périmètre » classé à la main. */
  aRegarder: boolean;
}

export const FILTRES_D_ATTENTE_VIDES: FiltresAttente = { sens: null, raison: null, aRegarder: false };

export function lireFiltresAttente(p: URLSearchParams): FiltresAttente {
  const sens = p.get('sens');
  const raison = p.get('raison');
  return {
    sens: sens === 'entree' || sens === 'sortie' ? sens : null,
    raison: raison && raison in RAISONS_D_ATTENTE ? raison : null,
    aRegarder: p.get('a_regarder') === '1',
  };
}

export function versParametresAttente(f: FiltresAttente): URLSearchParams {
  const p = new URLSearchParams();
  if (f.sens) p.set('sens', f.sens);
  if (f.raison) p.set('raison', f.raison);
  if (f.aRegarder) p.set('a_regarder', '1');
  return p;
}
